import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, BladApi } from '../api/client';
import { useLista } from '../api/lista';
import type { Grupa, Indeks, Lista, Lokalizacja, RodzajIndeksu } from '../api/typy';
import { useAuth } from '../auth/AuthContext';
import {
  AtrapaLeku, Karta, Komunikat, Ladowanie, Miejsce, Plakietka, Pole, PoleTekstowe,
  Przelacznik, Przycisk, Pusto, Wybor, Zakladki,
} from '../components/ui';
import { PasekWspolpracy } from '../components/PasekWspolpracy';
import { nazwaZListy, useEdycjaWspolna } from '../lib/wspolna-edycja';
import { PrzyciskExcel } from '../components/PrzyciskExcel';
import { ilosc, zlote } from '../lib/format';

type Filtr = { szukaj: string; type: RodzajIndeksu | ''; tylkoAktywne: boolean };

export function Kartoteka() {
  const { maUprawnienie } = useAuth();
  const mozeEdytowac = maUprawnienie('kartoteka.zapis');
  const widziCeny = maUprawnienie('ceny.odczyt');

  const [filtr, setFiltr] = useState<Filtr>({ szukaj: '', type: 'MATERIAL', tylkoAktywne: true });
  const [edytowany, setEdytowany] = useState<Indeks | 'nowy' | null>(null);
  const [skan, setSkan] = useState<string | null>(null);

  const parametry = new URLSearchParams({
    ...(filtr.szukaj ? { szukaj: filtr.szukaj } : {}),
    ...(filtr.type ? { type: filtr.type } : {}),
    ...(filtr.tylkoAktywne ? { tylkoAktywne: 'true' } : {}),
    naStronie: '100',
  });

  const lista = useQuery({
    queryKey: ['kartoteka', parametry.toString()],
    queryFn: () => api.get<Lista<Indeks>>(`/kartoteka?${parametry}`),
  });

  /**
   * Skaner USB pracuje jako klawiatura: wpisuje kod i wciska Enter. Jeśli
   * wpisany ciąg trafi w dokładnie jeden indeks, otwieramy go od razu —
   * magazynier nie musi niczego wybierać z listy.
   */
  async function obsluzSkan(e: FormEvent) {
    e.preventDefault();
    const kod = filtr.szukaj.trim();
    if (!kod) return;
    try {
      const trafiony = await api.get<Indeks>(`/kartoteka/skan/${encodeURIComponent(kod)}`);
      setEdytowany(trafiony);
      setSkan(null);
    } catch (err) {
      setSkan(err instanceof BladApi ? err.message : 'Nie znaleziono pozycji o tym kodzie.');
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-kamien-900">Kartoteka</h1>
          <p className="mt-1 text-sm text-kamien-500">
            Materiały zużywalne rozliczamy ilościowo, sprzęt — pojedynczymi egzemplarzami.
          </p>
        </div>
        <PrzyciskExcel dzial="kartoteka" parametry={parametry} className="ml-auto" />
        {mozeEdytowac && (
          <Przycisk wariant="glowny" onClick={() => setEdytowany('nowy')}>Nowy indeks</Przycisk>
        )}
      </div>

      <Zakladki
        wartosc={filtr.type}
        zmien={(v) => setFiltr({ ...filtr, type: v })}
        opcje={[
          { wartosc: 'MATERIAL', etykieta: 'Materiały zużywalne' },
          { wartosc: 'EQUIPMENT', etykieta: 'Typy sprzętu' },
          { wartosc: '', etykieta: 'Wszystko' },
        ]}
      />

      <form onSubmit={obsluzSkan} className="flex flex-wrap items-end gap-3">
        <div className="min-w-64 flex-1">
          <Pole
            etykieta="Szukaj lub zeskanuj kod"
            value={filtr.szukaj}
            onChange={(e) => { setFiltr({ ...filtr, szukaj: e.target.value }); setSkan(null); }}
            placeholder="Kod indeksu, nazwa albo kod kreskowy"
            podpowiedz="Skaner USB: ustaw kursor tutaj i zeskanuj — Enter otworzy pozycję."
            autoFocus
          />
        </div>
        <label className="flex items-center gap-2 pb-3 text-sm text-kamien-700">
          <input
            type="checkbox" className="size-4 accent-bordo-600"
            checked={filtr.tylkoAktywne}
            onChange={(e) => setFiltr({ ...filtr, tylkoAktywne: e.target.checked })}
          />
          tylko czynne
        </label>
      </form>

      {skan && <Komunikat rodzaj="uwaga">{skan}</Komunikat>}

      {edytowany && (
        <FormularzIndeksu
          key={edytowany === 'nowy' ? 'nowy' : edytowany.id}
          indeks={edytowany === 'nowy' ? null : edytowany}
          widziCeny={widziCeny}
          zamknij={() => setEdytowany(null)}
        />
      )}

      {lista.isLoading ? <Ladowanie /> : (
        <Karta>
          {lista.data && lista.data.pozycje.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-sm">
                <thead className="border-b border-kamien-200 text-left text-xs uppercase tracking-wide text-kamien-500">
                  <tr>
                    <th className="px-5 py-3 font-medium">Kod</th>
                    <th className="px-5 py-3 font-medium">Nazwa</th>
                    <th className="px-5 py-3 font-medium">Jedn.</th>
                    <th className="px-5 py-3 text-right font-medium">Min</th>
                    <th className="px-5 py-3 text-right font-medium">Max</th>
                    {widziCeny && <th className="px-5 py-3 text-right font-medium">Cena zakupu</th>}
                    <th className="px-5 py-3 font-medium">Lokalizacja</th>
                    <th className="px-5 py-3 font-medium">Cechy</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-kamien-200">
                  {lista.data.pozycje.map((p) => (
                    <tr
                      key={p.id}
                      onClick={() => mozeEdytowac && setEdytowany(p)}
                      className={`${mozeEdytowac ? 'cursor-pointer hover:bg-kamien-50' : ''} ${p.isActive ? '' : 'text-kamien-400'}`}
                    >
                      <td className="px-5 py-3 font-mono text-xs text-kamien-900">{p.code}</td>
                      <td className="px-5 py-3">
                        <span className="text-kamien-900">{p.name}</span>
                        {p.isDrugDummy && <span className="ml-2"><AtrapaLeku /></span>}
                      </td>
                      <td className="px-5 py-3 text-kamien-600">{p.unit}</td>
                      <td className="px-5 py-3 text-right text-kamien-700">{p.minQty ? ilosc(p.minQty) : '—'}</td>
                      <td className="px-5 py-3 text-right text-kamien-700">{p.maxQty ? ilosc(p.maxQty) : '—'}</td>
                      {widziCeny && (
                        <td className="px-5 py-3 text-right text-kamien-700">{zlote(p.purchasePrice)}</td>
                      )}
                      <td className="px-5 py-3 text-xs"><Miejsce lokalizacja={p.defaultLocation} /></td>
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {!p.isActive && <Plakietka>wyłączony</Plakietka>}
                          {p.tracksBatches && <Plakietka ton="uwaga">partie</Plakietka>}
                          {p.type === 'EQUIPMENT' && <Plakietka>{p._count.units} egz.</Plakietka>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="border-t border-kamien-200 px-5 py-3 text-sm text-kamien-500">
                {lista.data.razem} pozycji
              </p>
            </div>
          ) : (
            <Pusto
              tytul="Nic tu jeszcze nie ma"
              opis={filtr.szukaj ? 'Żaden indeks nie pasuje do wyszukiwania.' : 'Kartoteka jest pusta. Zacznij od założenia pierwszego indeksu.'}
              akcja={mozeEdytowac && !filtr.szukaj
                ? <Przycisk wariant="glowny" onClick={() => setEdytowany('nowy')}>Nowy indeks</Przycisk>
                : undefined}
            />
          )}
        </Karta>
      )}
    </div>
  );
}

const PUSTY = {
  code: '', name: '', type: 'MATERIAL' as RodzajIndeksu, unit: 'szt.', ean: '',
  groupId: '', defaultLocationId: '', minQty: '', maxQty: '', purchasePrice: '',
  packageQty: '', tracksBatches: false, isDrugDummy: false, isActive: true, notes: '',
};

function FormularzIndeksu({ indeks, widziCeny, zamknij }: {
  indeks: Indeks | null; widziCeny: boolean; zamknij: () => void;
}) {
  const qc = useQueryClient();
  const edycja = useEdycjaWspolna<Indeks, typeof PUSTY>({
    encja: 'kartoteka', rekord: indeks, pusty: PUSTY,
    doFormularza: (i) => ({
      code: i.code, name: i.name, type: i.type, unit: i.unit,
      ean: i.ean ?? '', groupId: String(i.group?.id ?? ''),
      defaultLocationId: String(i.defaultLocation?.id ?? ''),
      minQty: i.minQty ?? '', maxQty: i.maxQty ?? '',
      purchasePrice: i.purchasePrice ?? '', packageQty: i.packageQty ?? '',
      tracksBatches: i.tracksBatches, isDrugDummy: i.isDrugDummy,
      isActive: i.isActive, notes: i.notes ?? '',
    }),
    wczytaj: (id) => api.get<Indeks>(`/kartoteka/${id}`),
    etykiety: {
      code: 'Kod indeksu', name: 'Nazwa', type: 'Rodzaj', unit: 'Jednostka', ean: 'Kod kreskowy (EAN)',
      groupId: 'Grupa', defaultLocationId: 'Domyślna lokalizacja', minQty: 'Minimum', maxQty: 'Maksimum',
      purchasePrice: 'Cena zakupu', packageQty: 'Sztuk w opakowaniu', tracksBatches: 'Partie i terminy ważności',
      isDrugDummy: 'Atrapa leku', isActive: 'Pozycja czynna', notes: 'Uwagi',
    },
    pokazWartosc: (pole, v) => {
      if (pole === 'groupId') return nazwaZListy(grupy.pozycje, v, (g) => `${g.code} — ${g.name}`);
      if (pole === 'defaultLocationId') return nazwaZListy(lokalizacje.pozycje, v, (l) => l.path ?? l.name);
      return undefined;
    },
  });
  const { dane, ustaw: setDane } = edycja;
  const [bledy, setBledy] = useState<Record<string, string>>({});
  const [blad, setBlad] = useState<string | null>(null);

  const grupy = useLista({ queryKey: ['grupy'], queryFn: () => api.get<{ pozycje: Grupa[] }>('/grupy') });
  const lokalizacje = useLista({
    queryKey: ['lokalizacje', 'skladowe'],
    queryFn: () => api.get<{ pozycje: Lokalizacja[] }>('/lokalizacje?tylkoSkladowe=true&tylkoAktywne=true'),
  });

  const zapisz = useMutation({
    mutationFn: () => edycja.zapisz((d, wersja) => {
      const cialo = {
        ...d,
        groupId: d.groupId || null,
        defaultLocationId: d.defaultLocationId || null,
        ...(indeks ? { version: wersja } : {}),
      };
      return indeks
        ? api.put<Indeks>(`/kartoteka/${indeks.id}`, cialo)
        : api.post<Indeks>('/kartoteka', cialo);
    }),
    onSuccess: (zapisany) => {
      void qc.invalidateQueries({ queryKey: ['kartoteka'] });
      void qc.invalidateQueries({ queryKey: ['lokalizacja'] });
      edycja.poZapisie(zapisany, zamknij);
    },
    onError: (e) => {
      if (e instanceof BladApi) {
        setBledy(e.bledyPol);
        if (Object.keys(e.bledyPol).length === 0) setBlad(e.message);
      }
    },
  });

  const materiał = dane.type === 'MATERIAL';

  return (
    <Karta
      tytul={indeks ? `Indeks ${indeks.code}` : 'Nowy indeks'}
      opis={indeks ? `Wersja ${indeks.version} · ruchów: ${indeks._count.movements}` : undefined}
    >
      <form
        className="grid gap-4 px-5 py-5 sm:grid-cols-2 lg:grid-cols-3"
        onSubmit={(e) => { e.preventDefault(); setBlad(null); setBledy({}); zapisz.mutate(); }}
      >
        {blad && <div className="sm:col-span-2 lg:col-span-3"><Komunikat rodzaj="blad">{blad}</Komunikat></div>}
        <PasekWspolpracy edycja={edycja} />

        <Pole etykieta="Kod indeksu" value={dane.code} blad={bledy.code}
          onChange={(e) => setDane({ ...dane, code: e.target.value })} required />
        <div className="sm:col-span-1 lg:col-span-2">
          <Pole etykieta="Nazwa" value={dane.name} blad={bledy.name}
            onChange={(e) => setDane({ ...dane, name: e.target.value })} required />
        </div>

        <Wybor etykieta="Rodzaj" value={dane.type} blad={bledy.type}
          onChange={(e) => setDane({ ...dane, type: e.target.value as RodzajIndeksu, tracksBatches: false })}
          disabled={!!indeks && indeks._count.movements + indeks._count.units + indeks._count.batches > 0}
        >
          <option value="MATERIAL">Materiał zużywalny</option>
          <option value="EQUIPMENT">Sprzęt (ewidencja egzemplarzy)</option>
        </Wybor>
        <Pole etykieta="Jednostka" value={dane.unit} blad={bledy.unit}
          onChange={(e) => setDane({ ...dane, unit: e.target.value })} required />
        <Pole etykieta="Kod kreskowy (EAN)" value={dane.ean} blad={bledy.ean}
          onChange={(e) => setDane({ ...dane, ean: e.target.value })}
          podpowiedz="8–14 cyfr, opcjonalnie" />

        <Wybor etykieta="Grupa" value={dane.groupId} blad={bledy.groupId ?? grupy.blad}
          onChange={(e) => setDane({ ...dane, groupId: e.target.value })}>
          <option value="">— bez grupy —</option>
          {grupy.pozycje.map((g) => <option key={g.id} value={g.id}>{g.code} — {g.name}</option>)}
        </Wybor>
        <div className="sm:col-span-1 lg:col-span-2">
          <Wybor etykieta="Domyślna lokalizacja" value={dane.defaultLocationId}
            blad={bledy.defaultLocationId ?? lokalizacje.blad}
            onChange={(e) => setDane({ ...dane, defaultLocationId: e.target.value })}>
            <option value="">— nie wskazano —</option>
            {lokalizacje.pozycje.map((l) => (
              <option key={l.id} value={l.id}>{l.code} — {l.path ?? l.name}</option>
            ))}
          </Wybor>
        </div>

        {materiał && (
          <>
            <Pole etykieta="Minimum" type="number" step="0.001" min="0" value={dane.minQty} blad={bledy.minQty}
              onChange={(e) => setDane({ ...dane, minQty: e.target.value })}
              podpowiedz="Poniżej tej ilości pozycja trafia na listę zakupową" />
            <Pole etykieta="Maksimum" type="number" step="0.001" min="0" value={dane.maxQty} blad={bledy.maxQty}
              onChange={(e) => setDane({ ...dane, maxQty: e.target.value })} />
            <Pole etykieta="Sztuk w opakowaniu" type="number" step="0.001" min="0" value={dane.packageQty} blad={bledy.packageQty}
              onChange={(e) => setDane({ ...dane, packageQty: e.target.value })}
              podpowiedz="Przyjmujemy opakowaniami, wydajemy sztukami" />
          </>
        )}

        {widziCeny && (
          <Pole etykieta="Cena zakupu (zł)" type="number" step="0.01" min="0" value={dane.purchasePrice}
            blad={bledy.purchasePrice}
            onChange={(e) => setDane({ ...dane, purchasePrice: e.target.value })}
            podpowiedz="Wartość ewidencyjna; koszt rzeczywisty bierzemy z partii" />
        )}

        <div className="sm:col-span-2 lg:col-span-3">
          <PoleTekstowe etykieta="Uwagi" value={dane.notes} blad={bledy.notes}
            onChange={(e) => setDane({ ...dane, notes: e.target.value })} />
        </div>

        <div className="flex flex-col gap-1 sm:col-span-2 lg:col-span-3">
          {materiał && (
            <Przelacznik
              etykieta="Prowadź partie i terminy ważności"
              opis="Rozchód pójdzie według FEFO — najpierw najkrótszy termin"
              checked={dane.tracksBatches}
              onChange={(e) => setDane({ ...dane, tracksBatches: e.target.checked })}
            />
          )}
          <Przelacznik
            etykieta="Atrapa leku"
            opis="Ostrzeżenie „nie do użytku klinicznego” pojawi się na kartotece i na wydaniu"
            checked={dane.isDrugDummy}
            onChange={(e) => setDane({ ...dane, isDrugDummy: e.target.checked })}
          />
          <Przelacznik
            etykieta="Pozycja czynna"
            opis="Wyłączona znika z list wyboru, ale zostaje w historii"
            checked={dane.isActive}
            onChange={(e) => setDane({ ...dane, isActive: e.target.checked })}
          />
        </div>

        <div className="flex gap-3 sm:col-span-2 lg:col-span-3">
          <Przycisk type="submit" wariant="glowny" disabled={zapisz.isPending}>
            {zapisz.isPending ? 'Zapisywanie...' : indeks ? 'Zapisz zmiany' : 'Załóż indeks'}
          </Przycisk>
          <Przycisk type="button" wariant="cichy" onClick={zamknij}>Anuluj</Przycisk>
        </div>
      </form>
    </Karta>
  );
}
